import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Network, Server, Activity, ArrowRight, RefreshCw, Database,
  CheckCircle2, AlertTriangle, XCircle, Cpu, Clock, Zap,
  GitBranch, Shield, Layers, ChevronDown, ChevronUp, Search
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '@/config/api';

// ─── Status helpers ──────────────────────────────────────────
const statusConfig = {
  healthy: { color: 'bg-emerald-500', text: 'text-emerald-700', bg: 'bg-emerald-50', icon: CheckCircle2, label: 'Healthy' },
  degraded: { color: 'bg-amber-500', text: 'text-amber-700', bg: 'bg-amber-50', icon: AlertTriangle, label: 'Degraded' },
  down: { color: 'bg-red-500', text: 'text-red-700', bg: 'bg-red-50', icon: XCircle, label: 'Down' },
  unknown: { color: 'bg-gray-400', text: 'text-gray-600', bg: 'bg-gray-50', icon: Server, label: 'Unknown' },
};

function StatusDot({ status, size = 'sm' }) {
  const cfg = statusConfig[status] || statusConfig.unknown;
  const cls = size === 'lg' ? 'h-3.5 w-3.5' : 'h-2.5 w-2.5';
  return <span className={`inline-block rounded-full ${cls} ${cfg.color} ${status === 'healthy' ? '' : 'animate-pulse'}`} />;
}

function timeAgo(isoStr) {
  if (!isoStr) return 'never';
  const diff = Date.now() - new Date(isoStr).getTime();
  const secs = Math.floor(diff / 1000);
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  return `${Math.floor(mins / 60)}h ago`;
}

// ─── Summary Strip ───────────────────────────────────────────
function SummaryStrip({ summary }) {
  const items = [
    { label: 'Services', value: summary.total_services, icon: Server, color: 'text-blue-600', bg: 'bg-blue-50' },
    { label: 'Healthy', value: summary.healthy, icon: CheckCircle2, color: 'text-emerald-600', bg: 'bg-emerald-50' },
    { label: 'Degraded', value: summary.degraded, icon: AlertTriangle, color: summary.degraded > 0 ? 'text-amber-600' : 'text-gray-400', bg: summary.degraded > 0 ? 'bg-amber-50' : 'bg-gray-50' },
    { label: 'Workers', value: summary.total_workers, icon: Cpu, color: 'text-violet-600', bg: 'bg-violet-50' },
    { label: 'Avg Latency', value: `${summary.avg_response_ms}ms`, icon: Clock, color: 'text-indigo-600', bg: 'bg-indigo-50' },
    { label: 'Connections', value: summary.total_edges, icon: GitBranch, color: 'text-teal-600', bg: 'bg-teal-50' },
    { label: 'Schemas', value: summary.schemas, icon: Database, color: 'text-orange-600', bg: 'bg-orange-50' },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
      {items.map((item) => {
        const Icon = item.icon;
        return (
          <Card key={item.label} className="border-gray-100 shadow-sm" data-testid={`mesh-summary-${item.label.toLowerCase()}`}>
            <CardContent className="p-3 flex items-center gap-2.5">
              <div className={`${item.bg} p-2 rounded-lg`}>
                <Icon className={`h-4 w-4 ${item.color}`} />
              </div>
              <div>
                <p className="text-lg font-bold tracking-tight leading-none">{item.value}</p>
                <p className="text-[10px] text-muted-foreground mt-0.5">{item.label}</p>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

// ─── Dependency Map (Visual) ─────────────────────────────────
function DependencyMap({ nodes, edges, onSelectNode }) {
  // Layout: arrange nodes in tiers based on dependency depth
  const depthMap = {};
  const visited = new Set();

  function calcDepth(name, depth = 0) {
    if (visited.has(name)) return;
    visited.add(name);
    depthMap[name] = Math.max(depthMap[name] || 0, depth);
    const node = nodes.find(n => n.name === name);
    if (node) {
      node.dependencies.forEach(dep => calcDepth(dep, depth + 1));
    }
  }

  // Start from nodes that have no incoming edges
  const targets = new Set(edges.map(e => e.to));
  const roots = nodes.filter(n => !targets.has(n.name));
  roots.forEach(r => calcDepth(r.name, 0));
  // Handle any remaining
  nodes.forEach(n => { if (!depthMap[n.name]) depthMap[n.name] = 2; });

  // Group by depth
  const tiers = {};
  nodes.forEach(n => {
    const d = depthMap[n.name] || 0;
    (tiers[d] = tiers[d] || []).push(n);
  });

  const tierKeys = Object.keys(tiers).sort((a, b) => a - b);
  const tierLabels = ['Entrypoints', 'Core Services', 'Infrastructure', 'Utilities'];

  return (
    <Card className="border-gray-100 shadow-sm" data-testid="dependency-map">
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <Network className="h-4 w-4 text-blue-600" />
          Service Dependency Map
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-4">
        <div className="space-y-6">
          {tierKeys.map((tierIdx, i) => (
            <div key={tierIdx}>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-2">
                {tierLabels[i] || `Tier ${tierIdx}`}
              </p>
              <div className="flex flex-wrap gap-2">
                {tiers[tierIdx].map((node) => (
                  <button
                    key={node.name}
                    onClick={() => onSelectNode(node)}
                    className="group flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50/50 transition-all"
                    data-testid={`mesh-node-${node.name}`}
                  >
                    <StatusDot status={node.status} />
                    <span className="text-xs font-medium group-hover:text-blue-700 transition-colors">
                      {node.name.replace('-service', '')}
                    </span>
                    {node.dependencies.length > 0 && (
                      <span className="text-[9px] text-muted-foreground bg-gray-100 px-1.5 py-0.5 rounded-full">
                        {node.dependencies.length}
                      </span>
                    )}
                  </button>
                ))}
              </div>
              {i < tierKeys.length - 1 && (
                <div className="flex items-center justify-center mt-3">
                  <div className="flex items-center gap-1 text-gray-300">
                    <div className="h-px w-8 bg-gray-200" />
                    <ArrowRight className="h-3 w-3" />
                    <div className="h-px w-8 bg-gray-200" />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Edge summary */}
        <Separator className="my-4" />
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-muted-foreground">
          {edges.map((e, i) => (
            <span key={i}>
              <span className="font-medium text-gray-600">{e.from.replace('-service', '')}</span>
              <ArrowRight className="h-2.5 w-2.5 inline mx-0.5" />
              <span className="font-medium text-gray-600">{e.to.replace('-service', '')}</span>
            </span>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Service Table ───────────────────────────────────────────
function ServiceTable({ nodes, search, onSearch, onSelect }) {
  const filtered = search
    ? nodes.filter(n => n.name.toLowerCase().includes(search.toLowerCase()) || n.schema.toLowerCase().includes(search.toLowerCase()))
    : nodes;

  return (
    <Card className="border-gray-100 shadow-sm" data-testid="service-table">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Layers className="h-4 w-4 text-violet-600" />
            All Services
          </CardTitle>
          <div className="relative w-56">
            <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => onSearch(e.target.value)}
              placeholder="Filter services..."
              className="h-8 pl-8 text-xs"
              data-testid="mesh-search"
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-0 pb-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/60">
                <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Service</th>
                <th className="px-4 py-2.5 text-center font-medium text-muted-foreground">Status</th>
                <th className="px-4 py-2.5 text-center font-medium text-muted-foreground">Schema</th>
                <th className="px-4 py-2.5 text-center font-medium text-muted-foreground">Latency</th>
                <th className="px-4 py-2.5 text-center font-medium text-muted-foreground">Uptime</th>
                <th className="px-4 py-2.5 text-center font-medium text-muted-foreground">Workers</th>
                <th className="px-4 py-2.5 text-center font-medium text-muted-foreground">Auto-Scale</th>
                <th className="px-4 py-2.5 text-center font-medium text-muted-foreground">Deps</th>
                <th className="px-4 py-2.5 text-right font-medium text-muted-foreground">Checked</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((node) => {
                const stCfg = statusConfig[node.status] || statusConfig.unknown;
                return (
                  <tr
                    key={node.name}
                    onClick={() => onSelect(node)}
                    className="border-b border-gray-50 hover:bg-gray-50/70 cursor-pointer transition-colors"
                    data-testid={`mesh-row-${node.name}`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <StatusDot status={node.status} />
                        <div>
                          <p className="font-medium text-sm">{node.name}</p>
                          <p className="text-[10px] text-muted-foreground">:{node.port}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Badge className={`${stCfg.bg} ${stCfg.text} border-0 text-[10px]`}>
                        {stCfg.label}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Badge variant="outline" className="text-[10px] font-mono">
                        {node.schema}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`text-xs font-semibold ${
                        node.metrics.avg_response_ms > 500 ? 'text-red-600' :
                        node.metrics.avg_response_ms > 200 ? 'text-amber-600' : 'text-emerald-600'
                      }`}>
                        {Math.round(node.metrics.avg_response_ms)}ms
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-1.5">
                        <Progress value={node.metrics.uptime_1h} className="h-1.5 w-12" />
                        <span className="text-xs">{node.metrics.uptime_1h}%</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center text-xs">
                      {node.workers}/{node.max_workers}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {node.auto_scaling ? (
                        <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px]">ON</Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px] text-gray-500">OFF</Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center text-xs text-muted-foreground">
                      {node.dependencies.length}
                    </td>
                    <td className="px-4 py-3 text-right text-[11px] text-muted-foreground">
                      {timeAgo(node.last_check)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Service Detail Dialog ───────────────────────────────────
function ServiceDetail({ node, open, onClose }) {
  if (!node) return null;
  const stCfg = statusConfig[node.status] || statusConfig.unknown;
  const Icon = stCfg.icon;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="mesh-detail-dialog" aria-describedby="mesh-detail-desc">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <StatusDot status={node.status} size="lg" />
            {node.name}
            <Badge variant="outline" className="text-xs ml-auto">:{node.port}</Badge>
          </DialogTitle>
          <p id="mesh-detail-desc" className="sr-only">Service mesh details</p>
        </DialogHeader>

        {/* Status banner */}
        <div className={`${stCfg.bg} rounded-lg p-3 flex items-center gap-2`}>
          <Icon className={`h-5 w-5 ${stCfg.text}`} />
          <span className={`font-medium text-sm ${stCfg.text}`}>{stCfg.label}</span>
          <span className="text-xs text-muted-foreground ml-auto">
            Last check: {timeAgo(node.last_check)}
          </span>
        </div>

        {/* Metrics grid */}
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: 'Avg Latency', value: `${Math.round(node.metrics.avg_response_ms)}ms` },
            { label: 'Min / Max', value: `${Math.round(node.metrics.min_response_ms)} / ${Math.round(node.metrics.max_response_ms)}ms` },
            { label: 'Uptime (1h)', value: `${node.metrics.uptime_1h}%` },
            { label: 'Workers', value: `${node.workers}/${node.max_workers}` },
            { label: 'Health Checks', value: node.metrics.checks_1h },
            { label: 'Scale Events', value: node.metrics.scaling_events_1h },
          ].map((m) => (
            <div key={m.label} className="bg-gray-50 rounded-lg p-2.5 text-center">
              <p className="text-sm font-bold">{m.value}</p>
              <p className="text-[10px] text-muted-foreground">{m.label}</p>
            </div>
          ))}
        </div>

        <Separator />

        {/* Schema info */}
        <div className="flex items-center gap-3">
          <Database className="h-4 w-4 text-orange-500" />
          <div>
            <p className="text-sm font-medium">Database Schema</p>
            <p className="text-xs text-muted-foreground font-mono">{node.schema}</p>
          </div>
          <Badge className="ml-auto bg-orange-50 text-orange-700 border-0 text-[10px]">
            Isolated
          </Badge>
        </div>

        {/* Dependencies */}
        {node.dependencies.length > 0 && (
          <>
            <Separator />
            <div>
              <p className="text-sm font-medium mb-2 flex items-center gap-1.5">
                <GitBranch className="h-3.5 w-3.5" />
                Dependencies ({node.dependencies.length})
              </p>
              <div className="flex flex-wrap gap-1.5">
                {node.dependencies.map((dep) => (
                  <Badge key={dep} variant="outline" className="text-xs">
                    {dep.replace('-service', '')}
                  </Badge>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Config */}
        <Separator />
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground">Auto-Scaling</span>
          <Badge className={node.auto_scaling ? 'bg-emerald-100 text-emerald-700 border-0' : ''} variant={node.auto_scaling ? 'default' : 'outline'}>
            {node.auto_scaling ? 'Enabled' : 'Disabled'}
          </Badge>
        </div>
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground">Priority</span>
          <span className="font-medium">{node.priority}</span>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main Page ───────────────────────────────────────────────
export default function ServiceMeshPage() {
  const { token } = useOutletContext();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedNode, setSelectedNode] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const intervalRef = useRef(null);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchMesh = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const { data: mesh } = await axios.get(`${API}/scaling/mesh`, { headers });
      setData(mesh);
    } catch {
      if (!silent) toast.error('Failed to load mesh data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [headers]);

  useEffect(() => {
    fetchMesh();
    intervalRef.current = setInterval(() => fetchMesh(true), 30000);
    return () => clearInterval(intervalRef.current);
  }, [fetchMesh]);

  const handleSelectNode = (node) => {
    setSelectedNode(node);
    setDetailOpen(true);
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="mesh-loading">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-7 gap-3">
          {[...Array(7)].map((_, i) => <Skeleton key={i} className="h-16" />)}
        </div>
        <Skeleton className="h-64" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="service-mesh-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Network className="h-6 w-6 text-blue-600" />
            Service Mesh
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Monitor {data?.summary?.total_services || 0} microservices across {data?.summary?.schemas || 0} isolated database schemas
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => fetchMesh(true)}
          disabled={refreshing}
          data-testid="mesh-refresh-btn"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Summary */}
      {data?.summary && <SummaryStrip summary={data.summary} />}

      {/* Dependency Map */}
      {data?.nodes && (
        <DependencyMap
          nodes={data.nodes}
          edges={data.edges}
          onSelectNode={handleSelectNode}
        />
      )}

      {/* Service Table */}
      {data?.nodes && (
        <ServiceTable
          nodes={data.nodes}
          search={search}
          onSearch={setSearch}
          onSelect={handleSelectNode}
        />
      )}

      {/* Detail Dialog */}
      <ServiceDetail
        node={selectedNode}
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
      />

      {/* Footer */}
      <div className="flex items-center justify-between text-xs text-muted-foreground pt-2 border-t border-gray-100">
        <span>Auto-refresh every 30s</span>
        {data?.generated_at && (
          <span>Last updated: {new Date(data.generated_at).toLocaleTimeString()}</span>
        )}
      </div>
    </div>
  );
}
